import {
    isBooleanFactKey,
    isMultiValueFactKey,
    type PropertyFactKey,
} from 'src/services/api/properties/property-fact-keys';
import {
    isBooleanPropertyFact,
    isStringListPropertyFact,
    isStringPropertyFact,
    type PropertyFactValue,
} from 'src/services/api/properties/property-fact-values';
import type { IPropertyFact } from 'src/services/api/properties/types';

// react-hook-form rows hold strings for every fact; boolean facts use these two and are
// converted back to real JSON booleans on the way to the API.
export const BOOLEAN_FORM_TRUE = 'true';
export const BOOLEAN_FORM_FALSE = 'false';

export const BOOLEAN_FACT_OPTIONS = [
    { value: BOOLEAN_FORM_TRUE, label: 'Yes' },
    { value: BOOLEAN_FORM_FALSE, label: 'No' },
];

/**
 * Whether the editor can render and save this fact. Facts stored as numbers or objects
 * have no input, so they stay untouched rather than being flattened to text. A list is
 * editable only for a registered multi-value key, since that is what the save path
 * writes back as a list — any other key would keep just the first entry.
 */
export function isEditableFact(fact: IPropertyFact): boolean {
    return isStringPropertyFact(fact) || isBooleanPropertyFact(fact) ||
        (isStringListPropertyFact(fact) && isMultiValueFactKey(fact.key));
}

/**
 * The stored fact value as the inputs a form row holds — one per value, so a list fact
 * gets one input per entry — or null when the fact isn't editable.
 */
export function factToFormValues(fact: IPropertyFact): string[] | null {
    let formValues: string[] | null = null;
    if (isStringPropertyFact(fact)) {
        formValues = [fact.value];
    } else if (isBooleanPropertyFact(fact)) {
        formValues = [fact.value ? BOOLEAN_FORM_TRUE : BOOLEAN_FORM_FALSE];
    } else if (isStringListPropertyFact(fact)) {
        formValues = fact.value.length > 0 ? [...fact.value] : [''];
    }
    return formValues;
}

/**
 * A form row's inputs turned back into the JSON value the API should store: a real
 * boolean for boolean keys, a list of strings for multi-value keys, trimmed text otherwise.
 */
export function formValuesToApiValue(key: PropertyFactKey, formValues: string[]): PropertyFactValue {
    if (isBooleanFactKey(key)) {
        return formValues[0] === BOOLEAN_FORM_TRUE;
    }
    if (isMultiValueFactKey(key)) {
        return formValues.map((value) => value.trim()).filter((value) => value !== '');
    }
    return (formValues[0] ?? '').trim();
}

/**
 * Validation message for a row's value, or null when it's valid. Boolean keys must be
 * one of the two picker options so a half-filled row can't post an accidental `false`.
 * A multi-value row needs one non-empty entry; the blank ones are dropped on save.
 */
export function getFactValueError(key: PropertyFactKey | '', formValues: string[]): string | null {
    let error: string | null = null;
    if (isBooleanFactKey(key)) {
        if (formValues[0] !== BOOLEAN_FORM_TRUE && formValues[0] !== BOOLEAN_FORM_FALSE) {
            error = 'Select Yes or No';
        }
    } else if (formValues.every((value) => value.trim().length === 0)) {
        error = 'Value is required';
    }
    return error;
}
